import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { accountTypes } from '@nu-art/user-account-shared';
import { _className, Button, ComponentSync, Grid, LL_H_C, LL_V_L, ModuleFE_Thunderstorm, ModuleFE_Toaster, performAction, SimpleListAdapter, TS_DropDown, TS_Input, TS_PropRenderer } from '@nu-art/thunderstorm-frontend/index';
import { capitalizeFirstLetter, DateTimeFormat_yyyyMMDDTHHmmss, JwtTools, Year } from '@nu-art/ts-common';
import './Component_AccountEditor.scss';
import { TS_Icons } from '@nu-art/ts-styles';
import { ModuleFE_Account } from '../_entity.js';
export class Component_AccountEditor extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.sessions = [];
        state.isPreview = !!nextProps.isPreview;
        state.user = nextProps.user;
        const accountId = nextProps.user?._id;
        if (accountId)
            ModuleFE_Account._v1.getSessions({ _id: accountId }).execute(async (response) => {
                const sessions = await Promise.all(response.sessions.map(async (session) => {
                    const sessionJWT = await JwtTools.decode(session.sessionIdJwt);
                    return {
                        ...sessionJWT,
                        _id: session._id,
                        label: session.label,
                        deviceId: session.deviceId,
                        sessionIdJwt: session.sessionIdJwt,
                    };
                }));
                this.setState({ sessions });
            });
        return state;
    }
    //######################### Logic #########################
    addAccount = async () => {
        return performAction(async () => {
            const account = await ModuleFE_Account._v1.createAccount({
                password: this.state.password,
                type: this.state.type,
                email: this.state.email,
                passwordCheck: this.state.password
            }).executeSync();
            this.props.onComplete?.(account._id);
            this.setState({
                email: undefined,
                password: undefined,
                passwordCheck: undefined,
                type: undefined
            });
        }, {
            type: 'notification',
            notificationLabels: {
                inProgress: 'Creating Account',
                success: 'Account Created',
                failed: 'Failed Creating Account'
            }
        });
    };
    canCreate = () => {
        const baseConditions = !!(this.state.email && this.state.type);
        let extraConditions = true;
        if (this.state.type === 'user')
            extraConditions = !!this.state.password;
        return baseConditions && extraConditions;
    };
    //######################### Render #########################
    renderDropdown = () => {
        if (this.state.isPreview)
            return _jsx(TS_PropRenderer.Vertical, { label: 'User Type', children: _jsx("div", { children: capitalizeFirstLetter(this.state.user?.type ? this.state.user.type : '') }) });
        return _jsx(TS_PropRenderer.Vertical, { label: 'User Type', children: _jsx(TS_DropDown, { placeholder: 'account type', selected: this.state.type, adapter: SimpleListAdapter([...accountTypes], i => _jsx("div", { className: 'node-data', children: _jsx("span", { children: i.item }) })), onSelected: (type) => {
                    type === 'service' ? this.setState({
                        type,
                        password: undefined
                    }) : this.setState({ type });
                } }) });
    };
    renderInputs = () => {
        if (this.state.isPreview)
            return _jsxs(LL_H_C, { className: 'inputs-row', children: [_jsx(TS_PropRenderer.Vertical, { label: 'Email', children: _jsx("div", { children: this.state.user?.email }) }), this.state.user?.type !== 'service' && _jsx(TS_PropRenderer.Vertical, { label: 'Need To Set Password', children: _jsx("div", { children: this.state.user?._newPasswordRequired ? 'Yes' : 'No' }) })] });
        return _jsxs(LL_H_C, { className: 'inputs-row', children: [_jsx(TS_PropRenderer.Vertical, { label: 'Email', children: _jsx(TS_Input, { type: 'text', placeholder: 'Email', value: this.state.email, onBlur: (email) => this.setState({ email }) }) }), _jsx(TS_PropRenderer.Vertical, { disabled: !(this.state.type === 'user'), label: 'Temporary Password', children: _jsx(TS_Input, { disabled: !(this.state.type === 'user'), type: 'password', value: this.state.password, placeholder: 'Temporary Password', onBlur: (password) => this.setState({ password }) }) })] });
    };
    renderSubmitButton = () => {
        if (this.state.isPreview)
            return '';
        const disabled = !this.canCreate();
        const className = _className(disabled && 'disabled');
        return _jsx(Button, { variant: 'primary', className: className, disabled: disabled, onClick: this.addAccount, children: "Add Account" });
    };
    renderGenToken = () => {
        if (!this.state.isPreview || this.state.user?.type !== 'service')
            return;
        const options = [
            { label: '1 Year', ttl: 1 * Year },
            { label: '2 Year', ttl: 2 * Year },
            { label: '3 Year', ttl: 3 * Year },
            { label: '5 Year', ttl: 5 * Year },
            { label: '10 Year', ttl: 10 * Year },
        ];
        return _jsx(TS_PropRenderer.Vertical, { label: 'Generate New Token', children: _jsxs(LL_H_C, { className: 'gen-token-row', children: [_jsx(TS_PropRenderer.Vertical, { label: 'TTL', children: _jsx(TS_DropDown, { placeholder: 'Select Session TTL', selected: options.find(option => option.ttl === this.state.tokenTTL), adapter: SimpleListAdapter(options, item => _jsx(_Fragment, { children: item.item.label })), onSelected: option => this.setState({ tokenTTL: option.ttl }) }) }), _jsx(TS_PropRenderer.Vertical, { label: 'Label (Optional)', children: _jsx(TS_Input, { type: "text", value: this.state.tokenLabel, onBlur: tokenLabel => this.setState({ tokenLabel }) }) }), _jsx(Button, { variant: 'primary', onClick: async () => {
                            try {
                                const token = await ModuleFE_Account._v1.createToken({
                                    accountId: this.state.user._id,
                                    ttl: this.state.tokenTTL,
                                    label: this.state.tokenLabel
                                })
                                    .executeSync();
                                await ModuleFE_Thunderstorm.copyToClipboard(token.token);
                                ModuleFE_Toaster.toastSuccess('Token copied to clipboard');
                                this.reDeriveState();
                            }
                            catch (e) {
                                ModuleFE_Toaster.toastError(e.message);
                                this.logError(e);
                            }
                        }, children: "Generate Token" })] }) });
    };
    renderSessionGrid = () => {
        if (!this.state.sessions.length)
            return '';
        return _jsx(TS_PropRenderer.Vertical, { label: 'Sessions', children: _jsxs(Grid, { children: [_jsx("div", { className: 'grid-title', children: "Label" }), _jsx("div", { className: 'grid-title', children: "Created At" }), _jsx("div", { className: 'grid-title', children: "Expiry Date" }), _jsx("div", { className: 'grid-title', children: "Device Id" }), _jsx("div", { className: 'grid-title' }), this.state.sessions.map(session => {
                        try {
                            const createdAt = DateTimeFormat_yyyyMMDDTHHmmss.format(session.iat * 1000);
                            const validTill = DateTimeFormat_yyyyMMDDTHHmmss.format(session.exp * 1000);
                            return _jsxs(React.Fragment, { children: [_jsx(LL_H_C, { className: 'grid-cell', children: session.label ?? 'No Label' }), _jsx(LL_H_C, { className: 'grid-cell', children: `${createdAt}` }), _jsx(LL_H_C, { className: 'grid-cell', children: `${validTill}` }), _jsx(LL_H_C, { className: 'grid-cell', children: session.deviceId }), _jsx(TS_Icons.copy.component, { onClick: () => ModuleFE_Thunderstorm.copyToClipboard(session.sessionIdJwt) })] }, session._id);
                        }
                        catch (e) {
                            return '';
                        }
                    })] }) });
    };
    renderDescription = () => {
        if (!this.state.user?.description)
            return '';
        return _jsx(TS_PropRenderer.Vertical, { label: 'description', children: this.state.user.description });
    };
    render() {
        return _jsxs(LL_V_L, { className: 'account-editor', children: [_jsxs(LL_V_L, { className: 'editor-section', children: [this.renderDropdown(), this.renderInputs(), this.renderSubmitButton(), this.renderDescription()] }), _jsxs(LL_V_L, { className: 'editor-section', children: [this.renderSessionGrid(), this.renderGenToken()] })] });
    }
}
//# sourceMappingURL=Component_AccountEditor.js.map